/**
 * Shiprocket Fastrr — One-click checkout helper.
 *
 * The Shiprocket script is loaded globally in /public/index.html and exposes
 * `window.shiprocketCheckoutEvents.buyDirect(...)`. This helper:
 *   - Converts our cart shape to Shiprocket's expected `{variantId, quantity}`
 *   - Extracts the numeric variantId from Shopify's gid:// format
 *   - Re-validates each cart line against Shopify (variant IDs go stale when
 *     a merchant bulk-edits prices), so we never send dead IDs to Fastrr
 *   - Surfaces a friendly error if the script hasn't loaded yet
 */

import { fetchProductByHandle } from "@/lib/shopify";

export const SHIPROCKET_SELLER_DOMAIN =
  process.env.REACT_APP_SHIPROCKET_SELLER_DOMAIN || "6btdtv-cr.myshopify.com";

const extractVariantId = (gid) => {
  if (!gid) return null;
  const s = String(gid);
  // Shopify variant IDs look like: gid://shopify/ProductVariant/49851363721538
  const match = s.match(/(\d+)(?!.*\d)/);
  return match ? match[1] : s;
};

/** Wait briefly for the deferred Shiprocket script to attach to window. */
const waitForShiprocket = (timeoutMs = 4000) =>
  new Promise((resolve, reject) => {
    if (window.shiprocketCheckoutEvents?.buyDirect) return resolve(true);
    const start = Date.now();
    const tick = () => {
      if (window.shiprocketCheckoutEvents?.buyDirect) return resolve(true);
      if (Date.now() - start > timeoutMs)
        return reject(new Error("Shiprocket checkout script not loaded yet."));
      setTimeout(tick, 120);
    };
    tick();
  });

/**
 * Launch Shiprocket's one-click checkout drawer with the items currently
 * in the user's cart. Before launch, re-fetches every product by handle to
 * grab the current variantId — protects against stale IDs after a Shopify
 * bulk price edit or product re-import.
 */
export async function launchShiprocketCheckout({
  cart,
  couponCode,
  utmParams,
  cartAttributes,
} = {}) {
  if (!cart?.length) throw new Error("Cart is empty");

  // Re-validate each line's variantId in parallel
  const refreshed = await Promise.all(
    cart.map(async (line) => {
      try {
        const fresh = await fetchProductByHandle(line.id);
        if (!fresh?.variantId || fresh.available === false) return null;
        const variantId = extractVariantId(fresh.variantId);
        if (!variantId) return null;
        return { variantId, quantity: line.qty || 1 };
      } catch {
        return null;
      }
    }),
  );

  const products = refreshed.filter(Boolean);

  if (!products.length)
    throw new Error(
      "Your cart items are no longer available. Please refresh and re-add.",
    );

  await waitForShiprocket();

  // Per Shiprocket docs — type "cart" + products array.
  window.shiprocketCheckoutEvents.buyDirect({
    type: "cart",
    products,
    ...(couponCode ? { couponCode } : {}),
    ...(utmParams ? { utmParams } : {}),
    ...(cartAttributes ? { cartAttributes } : {}),
  });
}

/* ------------------------------------------------------------------ */
/*  Debug helper — Shiprocket support can run this from the browser   */
/*  console on any page (girlygrove.in) to verify the integration.    */
/*  Usage: window.testFastrrCheckout()                                */
/* ------------------------------------------------------------------ */
if (typeof window !== "undefined") {
  window.testFastrrCheckout = async function (sampleVariantId) {
    if (!window.shiprocketCheckoutEvents?.buyDirect) {
      console.error(
        "❌ Fastrr script not loaded. Check #sellerDomain input + script tag.",
      );
      return;
    }
    // Pick a known variant from the live store if none passed.
    const variantId = sampleVariantId || "44091062747179"; // Gold Fan Shell Crystal Studs
    console.log(
      "✅ Fastrr script ready. Calling buyDirect with variantId:",
      variantId,
    );
    window.shiprocketCheckoutEvents.buyDirect({
      type: "cart",
      products: [{ variantId, quantity: 1 }],
    });
  };
  // Also surface our higher-level helper for the support team to inspect.
  window.launchShiprocketCheckout = launchShiprocketCheckout;
}
